import React, { useState, useEffect } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Container,
  CssBaseline,
  ThemeProvider,
  createTheme,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Avatar,
  Divider,
  Alert,
  Snackbar,
  TextField,
  Button,
  Card,
  CardContent
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Chat as ChatIcon,
  AdminPanelSettings as AdminIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  Menu as MenuIcon,
  Inventory as InventoryIcon,
  Business as SupplierIcon
} from '@mui/icons-material';
import { GoogleAuth } from './components/GoogleAuth';
import { CEODashboard } from './components/CEODashboard';
import { CEOChatbot } from './components/CEOChatbot';
import { InventoryDashboard } from './modules/inventory/components/InventoryDashboard';
import { SupplierDashboard } from './modules/suppliers/components/SupplierDashboard';
import { BackendConnectionTest } from './components/BackendConnectionTest';
import { User, QueryResponse, DashboardMetrics } from './types';
import APIService, { setAdminToken } from './services/api';
import { authService } from './services/authService';

const theme = createTheme({
  palette: {
    primary: { main: '#1976d2' },
    secondary: { main: '#dc004e' },
  },
});

const drawerWidth = 240;

type View = 'dashboard' | 'inventory' | 'suppliers' | 'chatbot' | 'admin';

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [currentView, setCurrentView] = useState<View>('dashboard');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [adminKey, setAdminKey] = useState('');

  // Admin state
  const [kTitle, setKTitle] = useState('');
  const [kContent, setKContent] = useState('');
  const [kCategory, setKCategory] = useState('');
  const [kKeywords, setKKeywords] = useState('');
  const [kQuery, setKQuery] = useState('');
  const [kResults, setKResults] = useState<any[]>([]);

  const [sTitle, setSTitle] = useState('');
  const [sContent, setSContent] = useState('');
  const [sTags, setSTags] = useState('');
  const [sQuery, setSQuery] = useState('');
  const [sResults, setSResults] = useState<any[]>([]);

  useEffect(() => {
    // Debug logging
    console.log('App mounted. Checking for saved user...');
    
    // Try to initialize auth from auth service
    const authenticatedUser = authService.initializeAuth();
    if (authenticatedUser) {
      console.log('Found authenticated user:', authenticatedUser);
      setUser(authenticatedUser);
    } else {
      // Fallback to old localStorage method for backward compatibility
      const savedUser = localStorage.getItem('user');
      if (savedUser) {
        try { 
          const parsedUser = JSON.parse(savedUser);
          console.log('Found saved user (legacy):', parsedUser);
          setUser(parsedUser); 
        } catch (e) { 
          console.error('Error parsing saved user:', e);
          localStorage.removeItem('user');
          localStorage.removeItem('google_token');
        }
      } else {
        console.log('No saved user found');
      }
    }
    
    const savedAdmin = localStorage.getItem('admin_token');
    if (savedAdmin) { 
      setAdminKey(savedAdmin); 
      setAdminToken(savedAdmin); 
    }
  }, []);

  const handleLoginSuccess = (userData: User) => {
    console.log('Login successful:', userData);
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const handleLoginError = (error: string) => { 
    console.error('Login error:', error);
    setError(error); 
  };

  const handleLogout = () => {
    console.log('Logging out...');
    authService.logout();
    setUser(null);
    // Force reload to clear any cached state
    window.location.reload();
  };

  const handleQuery = async (query: string): Promise<QueryResponse> => {
    return await APIService.ceoChat(query, true);
  };

  const handleLoadMetrics = async (): Promise<DashboardMetrics> => {
    try {
      console.log('App: Attempting to load dashboard metrics...');
      const result = await APIService.getDashboardMetrics();
      console.log('App: Successfully loaded metrics:', result);
      return result;
    } catch (error) {
      console.error('App: Failed to load dashboard metrics:', error);
      // Return mock data as fallback
      const fallbackData = {
        todayRevenue: 0,
        weekRevenue: 0,
        monthRevenue: 0,
        pendingOrders: 0,
        channelBreakdown: [],
        revenueTrend: []
      };
      console.log('App: Returning fallback data:', fallbackData);
      return fallbackData;
    }
  };

  const handleCloseError = () => { setError(null); };

  const runAdmin = async (fn: () => Promise<any>) => {
    try {
      const res = await fn();
      return res;
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Admin request failed');
      throw e;
    }
  };

  const AdminPanel = () => (
    <Box>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Admin Settings</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter your ADMIN_API_TOKEN to enable admin routes (CRUD KB and Static Rules).
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField label="ADMIN_API_TOKEN" fullWidth size="small" type="password" value={adminKey} onChange={(e) => setAdminKey(e.target.value)} />
            <Button variant="contained" onClick={() => { setAdminToken(adminKey || null); localStorage.setItem('admin_token', adminKey); }}>Save</Button>
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Backend Connection Test</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Test the connection to your backend API endpoints to ensure they're working correctly.
          </Typography>
          <BackendConnectionTest />
        </CardContent>
      </Card>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Knowledge (sleekshop-knowledge)</Typography>
            <Box sx={{ display: 'grid', gap: 1 }}>
              <TextField label="Title" size="small" value={kTitle} onChange={(e) => setKTitle(e.target.value)} />
              <TextField label="Content (Markdown)" multiline minRows={4} size="small" value={kContent} onChange={(e) => setKContent(e.target.value)} />
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField label="Category" size="small" value={kCategory} onChange={(e) => setKCategory(e.target.value)} />
                <TextField label="Keywords (comma-separated)" size="small" fullWidth value={kKeywords} onChange={(e) => setKKeywords(e.target.value)} />
              </Box>
              <Button variant="contained" onClick={async () => {
                await runAdmin(() => APIService.adminCreateKnowledge({
                  title: kTitle,
                  content: kContent,
                  category: kCategory || undefined,
                  keywords: kKeywords ? kKeywords.split(',').map(s => s.trim()).filter(Boolean) : undefined,
                  source: 'admin'
                }));
                setKTitle(''); setKContent(''); setKCategory(''); setKKeywords('');
              }}>Create Knowledge</Button>
            </Box>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <TextField label="Search query" size="small" fullWidth value={kQuery} onChange={(e) => setKQuery(e.target.value)} />
              <Button variant="outlined" onClick={async () => {
                const data = await runAdmin(() => APIService.adminSearchKnowledge({ q: kQuery, page: 1, size: 20 }));
                setKResults(data?.items || data?.results || []);
              }}>Search</Button>
            </Box>
            <Box sx={{ maxHeight: 240, overflow: 'auto' }}>
              {kResults.map((r: any) => (
                <Box key={r.id || r._id} sx={{ py: 1, borderBottom: '1px solid #eee' }}>
                  <Typography variant="subtitle2">{r.title}</Typography>
                  <Typography variant="caption" color="text.secondary">{r.category} · {(r.keywords || []).join(', ')}</Typography>
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Static Rules (sleekshop-knowledge-static)</Typography>
            <Box sx={{ display: 'grid', gap: 1 }}>
              <TextField label="Title" size="small" value={sTitle} onChange={(e) => setSTitle(e.target.value)} />
              <TextField label="Content" multiline minRows={4} size="small" value={sContent} onChange={(e) => setSContent(e.target.value)} />
              <TextField label="Tags (comma-separated)" size="small" value={sTags} onChange={(e) => setSTags(e.target.value)} />
              <Button variant="contained" onClick={async () => {
                await runAdmin(() => APIService.adminCreateStaticRule({
                  title: sTitle,
                  content: sContent,
                  tags: sTags ? sTags.split(',').map(s => s.trim()).filter(Boolean) : undefined,
                  type: 'static_rule'
                }));
                setSTitle(''); setSContent(''); setSTags('');
              }}>Create Rule</Button>
            </Box>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <TextField label="Search query" size="small" fullWidth value={sQuery} onChange={(e) => setSQuery(e.target.value)} />
              <Button variant="outlined" onClick={async () => {
                const data = await runAdmin(() => APIService.adminListStaticRules({ q: sQuery, page: 1, size: 50 }));
                setSResults(data?.items || data?.results || []);
              }}>Search</Button>
            </Box>
            <Box sx={{ maxHeight: 240, overflow: 'auto' }}>
              {sResults.map((r: any) => (
                <Box key={r.id || r._id} sx={{ py: 1, borderBottom: '1px solid #eee' }}>
                  <Typography variant="subtitle2">{r.title}</Typography>
                  <Typography variant="caption" color="text.secondary">{(r.tags || []).join(', ')}</Typography>
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );

  // Temporary development bypass - remove this in production
  const isDevelopment = process.env.NODE_ENV === 'development';
  const hasDevBypass = localStorage.getItem('dev_bypass') === 'true';
  
  if (!user && !(isDevelopment && hasDevBypass)) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {isDevelopment && (
          <Box sx={{ position: 'fixed', top: 20, right: 20, zIndex: 9999 }}>
            <Button 
              variant="contained" 
              color="warning" 
              size="small"
              onClick={() => {
                localStorage.setItem('dev_bypass', 'true');
                window.location.reload();
              }}
            >
              Dev Mode: Skip Auth
            </Button>
          </Box>
        )}
        <GoogleAuth onSuccess={handleLoginSuccess} onError={handleLoginError} />
        <Snackbar open={!!error} autoHideDuration={6000} onClose={handleCloseError}>
          <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>{error}</Alert>
        </Snackbar>
      </ThemeProvider>
    );
  }

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, view: 'dashboard' as View },
    { text: 'Inventory', icon: <InventoryIcon />, view: 'inventory' as View },
    { text: 'Suppliers', icon: <SupplierIcon />, view: 'suppliers' as View },
    { text: 'AI Chatbot', icon: <ChatIcon />, view: 'chatbot' as View },
    { text: 'Admin', icon: <AdminIcon />, view: 'admin' as View }
  ];

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ display: 'flex' }}>
        <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
          <Toolbar>
            <IconButton color="inherit" aria-label="open drawer" edge="start" onClick={() => setDrawerOpen(!drawerOpen)} sx={{ mr: 2 }}>
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
              SleekShop CEO Dashboard
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ width: 32, height: 32 }}>
                {user?.picture ? (
                  <img src={user.picture} alt={user.name} style={{ width: '100%', height: '100%' }} />
                ) : (
                  <PersonIcon />
                )}
              </Avatar>
              <Typography variant="body2" sx={{ display: { xs: 'none', sm: 'block' } }}>
                {user?.name || 'Development Mode'}
              </Typography>
              <IconButton color="inherit" onClick={handleLogout}><LogoutIcon /></IconButton>
            </Box>
          </Toolbar>
        </AppBar>

        <Drawer
          variant="temporary"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{ display: { xs: 'block' }, '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth } }}
        >
          <Toolbar />
          <Box sx={{ overflow: 'auto' }}>
            <List>
              {menuItems.map((item) => (
                <ListItem key={item.text} disablePadding>
                  <ListItemButton onClick={() => { setCurrentView(item.view); setDrawerOpen(false); }} selected={currentView === item.view}>
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.text} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
            <Divider />
          </Box>
        </Drawer>

        <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
          <Toolbar />
          <Container maxWidth="xl">
            {currentView === 'dashboard' && (<CEODashboard onLoadMetrics={handleLoadMetrics} />)}
            {currentView === 'inventory' && (<InventoryDashboard />)}
            {currentView === 'suppliers' && (<SupplierDashboard />)}
            {currentView === 'chatbot' && (<Box sx={{ height: 'calc(100vh - 120px)' }}><CEOChatbot onQuery={handleQuery} /></Box>)}
            {currentView === 'admin' && (<AdminPanel />)}
          </Container>
        </Box>
      </Box>
      <Snackbar open={!!error} autoHideDuration={6000} onClose={handleCloseError}>
        <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>{error}</Alert>
      </Snackbar>
    </ThemeProvider>
  );
}

export default App;